import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { LIFECYCLE_STAGES, getLifecycleStage } from '../data/lifecycleStages';
import { getLifecyclePillarBySlug } from '../data/lifecyclePillarSEO';
import { JOURNEYS } from '../data/journeyConfig';
import { SEASONAL_TIPS } from '../data/seasonalHouseTips';
import { WORKSPACE_TOOLS, toolIconUrl } from '../data/workspaceTools';
import { openMatcher } from '../utils/openMatcher';
import {
  BUILDERS_MATCHER,
  SUPPLIERS_MATCHER,
  TRADES_MATCHER,
  PROS_MATCHER,
} from '../data/megaMenuMatchers';
import useJsonLd from '../hooks/useJsonLd';
import { BreadcrumbsContainer } from '../components/seo/Breadcrumbs';
import LastReviewedStamp from '../components/seo/LastReviewedStamp';
import BuildPilotVerdict from '../components/editorial/BuildPilotVerdict';
import BuildPilotOpinion from '../components/editorial/BuildPilotOpinion';
import { HbhVideoStrip } from '../components/watch/HbhVideoPlayer';
import AuthorByline from '../components/seo/AuthorByline';
import { findEpisodeForContext } from '../data/hbhEpisodeMatcher';
import CompleteYourProject from '../components/affiliate/CompleteYourProject';
import SponsoredSlot from '../components/ads/SponsoredSlot';
import { affiliateLifecycleStage } from '../lib/affiliateContext';

/**
 * LifecyclePillarPage - Crawlable counterpart to the LifecycleStageModal.
 *
 * The modal is a UX wedge. This page is the SEO wedge.
 *
 * Each of the 5 lifecycle stages (dream/plan/choose/build/own) gets a
 * dedicated landing page at /lifecycle/{urlSlug}. The page mirrors the
 * modal's project tiles, matchers and workspace tool grid so users can
 * navigate the same paths from a crawlable URL - and adds:
 *   • Direct-answer "Quick Answer" block (AI Overview extract format)
 *   • Editorial body content (h2 sections + paragraphs)
 *   • FAQ section with FAQPage schema (eligible for FAQ rich results)
 *   • Article + BreadcrumbList + HowTo schema graph
 *   • Last reviewed stamp (freshness signal)
 *
 * Content source: /data/lifecyclePillarSEO.js
 * Modal config:   /data/lifecycleStages.js
 */
const MATCHER_LABEL_TO_PAYLOAD = {
  'Shortlist a builder': BUILDERS_MATCHER,
  'Shortlist a tradie': TRADES_MATCHER,
  'Shortlist a supplier': SUPPLIERS_MATCHER,
  'Shortlist a professional': PROS_MATCHER,
  'Find a tradie for finishing jobs': TRADES_MATCHER,
  'Find a tradie for repairs': TRADES_MATCHER,
  'Find a supplier for late selections': SUPPLIERS_MATCHER,
  'Find an independent inspector': PROS_MATCHER,
  'Find an inspector': PROS_MATCHER,
  'Find a property lawyer': PROS_MATCHER,
  'Find a conveyancer': PROS_MATCHER,
  'Find a finance broker': PROS_MATCHER,
};

const MAINT_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MAINT_STATES = [{ code: 'SA', label: 'Adelaide / SA' }, { code: 'VIC', label: 'Melbourne / VIC' }];

const LifecyclePillarPage = () => {
  const { stageSlug: urlSlug } = useParams();
  const pillar = getLifecyclePillarBySlug(urlSlug);
  const stage = pillar ? getLifecycleStage(pillar.stageSlug) : null;
  const [openFaq, setOpenFaq] = useState(null);
  const [maintState, setMaintState] = useState('SA');

  const canonicalUrl = pillar
    ? `https://buildpilot.com.au/lifecycle/${pillar.urlSlug}`
    : 'https://buildpilot.com.au';

  // Always call useJsonLd (rules of hooks); pass empty array when stage/pillar missing.
  useJsonLd(
    pillar && stage
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': `${canonicalUrl}#article`,
            headline: pillar.h1,
            description: pillar.seoDescription,
            url: canonicalUrl,
            datePublished: '2026-03-01',
            dateModified: '2026-03-01',
            author: {
              '@type': 'Person',
              '@id': 'https://buildpilot.com.au/#founder',
              name: 'George Giannakakis',
              url: 'https://buildpilot.com.au/about/editorial-team',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BuildPilot',
              url: 'https://buildpilot.com.au',
              logo: {
                '@type': 'ImageObject',
                url: 'https://buildpilot.com.au/images/buildpilot-logo-2026.png',
              },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
            articleSection: 'Lifecycle Stage Guide',
            inLanguage: 'en-AU',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': `${canonicalUrl}#faq`,
            mainEntity: pillar.faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://buildpilot.com.au' },
              { '@type': 'ListItem', position: 2, name: 'Lifecycle stages', item: 'https://buildpilot.com.au/sitemap' },
              { '@type': 'ListItem', position: 3, name: stage.name, item: canonicalUrl },
            ],
          },
        ]
      : [],
    `lifecycle-pillar-${urlSlug || 'none'}`
  );

  if (!pillar) {
    return <Navigate to="/sitemap" replace />;
  }
  if (!stage) return <Navigate to="/" replace />;

  const journeys = (stage.projectTiles || [])
    .map((slug) => JOURNEYS.find((j) => j.slug === slug))
    .filter(Boolean);

  const toolById = (id) => WORKSPACE_TOOLS.find((t) => t.id === id);

  const matchers = [
    ...(stage.matcher ? [stage.matcher] : []),
    ...(stage.matchers || []),
  ];

  // Other lifecycle stages to cross-link at the bottom
  const stageSlugToUrlSlug = { dream: 'start-dreaming', plan: 'begin-planning', choose: 'make-choices', build: 'get-building', own: 'love-living' };
  const otherPillars = LIFECYCLE_STAGES
    .filter((s) => s.slug !== pillar.stageSlug)
    .map((s) => {
      const otherUrlSlug = stageSlugToUrlSlug[s.slug];
      const p = otherUrlSlug ? getLifecyclePillarBySlug(otherUrlSlug) : null;
      return p ? { stage: s, pillar: p } : null;
    })
    .filter(Boolean);

  const fireMatcher = (m) => {
    if (m.event && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(m.event));
      return;
    }
    const payload = MATCHER_LABEL_TO_PAYLOAD[m.label];
    if (payload) {
      openMatcher({ ...payload, sourcePlacement: `lifecycle-pillar:${pillar.stageSlug}` });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`lifecycle-pillar-${pillar.urlSlug}`}>
      <Helmet>
        <title>{pillar.seoTitle}</title>
        <meta name="description" content={pillar.seoDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pillar.seoTitle} />
        <meta property="og:description" content={pillar.seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://buildpilot.com.au/images/og-image-v2.png" />
        <meta property="og:locale" content="en_AU" />
        <meta property="og:site_name" content="BuildPilot" />
      </Helmet>

      <BreadcrumbsContainer
        items={[
          { name: 'Lifecycle stages', url: '/sitemap' },
          { name: stage.name },
        ]}
        variant="light"
      />

      {/* Header */}
      <header className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-6">
        <div className="flex items-center gap-2 mb-3">
          {stage.iconSrc && (
            <img
              src={stage.iconSrc}
              alt=""
              aria-hidden="true"
              className="w-7 h-7 object-contain"
              loading="eager"
            />
          )}
        </div>
        <h1 className="text-[32px] sm:text-[40px] lg:text-[50px] font-bold text-[#1d1d1f] leading-[1.05] tracking-tight max-w-3xl">
          {pillar.h1}
        </h1>
        <AuthorByline reviewedDate="2026-02-27" />
        <p className="text-[15px] sm:text-[17px] lg:text-[18px] text-[#5a5a5f] mt-4 sm:mt-5 max-w-2xl leading-relaxed">
          {pillar.intro}
        </p>
        <div className="mt-4">
          <LastReviewedStamp date="2026-03-01" className="!text-gray-500" />
        </div>
      </header>

      {/* BuildPilot verdict, top-of-page bottom-line in one sentence.
          Sets editorial tone immediately so the page never reads as an
          auto-generated reference page. */}
      {pillar.verdict && (
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8">
          <BuildPilotVerdict reviewedDate="2026-03-01">
            {pillar.verdict}
          </BuildPilotVerdict>
        </section>
      )}

      {/* Quick Answer block - AI Overview extract format */}
      <section
        className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-10"
        data-testid="quick-answer"
      >
        <div className="bg-[#f5f5f7] border border-[#dce5ff] rounded-2xl p-5 sm:p-7">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#1d8efe] mb-2">
            Quick answer
          </p>
          <p className="text-[15px] sm:text-[17px] text-[#1d1d1f] leading-relaxed">
            {pillar.quickAnswer}
          </p>
        </div>
      </section>

      {/* Primary action row - matchers (solid blue pills) */}
      {matchers.length > 0 && (
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="pillar-matchers">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 mb-3">
            Get matched in 60 seconds
          </h2>
          <div className="flex flex-wrap gap-2">
            {matchers.map((m) => (
              <button
                key={m.label}
                type="button"
                onClick={() => fireMatcher(m)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1d8efe] text-white rounded-full text-[12.5px] sm:text-[13.5px] font-semibold hover:opacity-90 transition-opacity"
                data-testid={`pillar-matcher-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {m.label}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Project tiles */}
      {journeys.length > 0 && (
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="pillar-projects">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 mb-3">
            What are you doing?
          </h2>
          <div className="flex flex-wrap gap-2">
            {journeys.map((j) => (
              <Link
                key={j.slug}
                to={`/journey/${j.slug}`}
                className="inline-flex items-center gap-1.5 pl-1.5 pr-3.5 py-1.5 bg-white border border-gray-200 hover:border-[#1d1d1f] rounded-full transition-colors"
              >
                <img src={j.iconSrc} alt="" aria-hidden="true" className="w-6 h-6 object-contain flex-shrink-0" loading="lazy" />
                <span className="text-[12.5px] font-semibold text-[#1d1d1f]">{j.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Body - editorial content (resource layer) */}
      <main className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {pillar.bodySections.map((sec, idx) => (
          <section key={idx} className="mb-10" data-testid={`body-section-${idx}`}>
            <h2 className="text-[22px] sm:text-[28px] font-bold text-[#1d1d1f] leading-tight mb-4">
              {sec.h2}
            </h2>
            {sec.paragraphs.map((p, pidx) => (
              <p key={pidx} className="text-[15px] sm:text-[16.5px] text-[#3a3a3f] leading-[1.7] mb-4">
                {p}
              </p>
            ))}
          </section>
        ))}

        {/* BuildPilot opinion, paired with the resource body above. The
            Resource + Review pattern: facts/regulations sit in the body,
            BuildPilot's editorial reading sits in this gradient callout. */}
        {pillar.buildpilotOpinion && (
          <section className="mb-10" data-testid="pillar-buildpilot-opinion">
            <BuildPilotOpinion>
              {pillar.buildpilotOpinion}
            </BuildPilotOpinion>
          </section>
        )}
      </main>

      {/* Modal-style sections from lifecycleStages (workspace tools + content links) */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-12 space-y-7">
        {stage.sections.map((section, idx) => (
          <div key={idx} data-testid={`stage-section-${idx}`}>
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 mb-3">
              {section.title}
            </h2>

            {section.workspaceToolIds && (
              <div className="flex flex-wrap gap-1.5 gap-y-2">
                {section.workspaceToolIds.map((id) => {
                  const tool = toolById(id);
                  if (!tool) return null;
                  return (
                    <Link
                      key={tool.id}
                      to={tool.route}
                      className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1 bg-[#f3eeff] hover:bg-[#e8dfff] border border-[#dcd0ff] hover:border-[#b9a3ff] rounded-full transition-colors"
                    >
                      <img src={toolIconUrl(tool)} alt="" aria-hidden="true" className="w-5 h-5 object-contain flex-shrink-0" loading="lazy" />
                      <span className="text-[12px] font-semibold text-[#3b2a7a]">
                        {tool.name}
                      </span>
                      {tool.premium && (
                        <span className="text-[9px] uppercase tracking-[0.07em] font-bold text-[#3b2a7a]/70 ml-0.5">
                          Pro
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}

            {section.links && (
              <div className="flex flex-wrap gap-1.5 gap-y-2">
                {section.links.map((l) => (
                  <Link
                    key={l.href}
                    to={l.href}
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 hover:border-[#1d1d1f] rounded-full text-[12px] text-gray-700 hover:text-[#1d1d1f] transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Seasonal maintenance grid - only for the 'own' (Love Living) stage.
          Generic month-by-month guidance, SA/VIC toggle. No fixed dates. */}
      {pillar.stageSlug === 'own' && (() => {
        const tips = SEASONAL_TIPS[maintState] || SEASONAL_TIPS.SA;
        return (
          <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="seasonal-maintenance">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-[22px] sm:text-[28px] font-bold text-[#1d1d1f] leading-tight">
                  Seasonal home maintenance
                </h2>
                <p className="text-[14px] text-gray-500 mt-1.5 max-w-2xl leading-relaxed">
                  A month-by-month guide to what's worth checking around a {maintState === 'VIC' ? 'Melbourne' : 'Adelaide'} home. General guidance — not a fixed schedule — so adapt it to your home and weather.
                </p>
              </div>
              <div className="inline-flex bg-gray-100 rounded-full p-1 self-start" data-testid="maint-state-toggle">
                {MAINT_STATES.map((s) => (
                  <button key={s.code} type="button" onClick={() => setMaintState(s.code)} data-testid={`maint-state-${s.code}`}
                    className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${maintState === s.code ? 'bg-white text-[#1d1d1f] shadow-sm' : 'text-gray-500 hover:text-[#1d1d1f]'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {MAINT_MONTHS.map((m, i) => {
                const t = tips[i];
                if (!t) return null;
                const title = t.title.replace(/^[A-Za-z]+:\s*/, '');
                return (
                  <div key={m} className="border border-gray-200 rounded-2xl p-5" data-testid={`maint-month-${i}`}>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1d8efe] mb-1">{m}</div>
                    <p className="text-[14px] font-semibold text-[#1d1d1f] leading-snug mb-1.5">{title}</p>
                    <p className="text-[13px] text-gray-500 leading-[1.6] mb-2">{t.body}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {(t.links || []).map((l) => (
                        <Link key={l.to + l.label} to={l.to} className="text-[12.5px] font-semibold text-[#1d8efe] hover:underline">{l.label} →</Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* FAQ section - visible + FAQPage schema */}
      <section className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-testid="pillar-faqs">
        <h2 className="text-[22px] sm:text-[30px] font-bold text-[#1d1d1f] leading-tight mb-6">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {pillar.faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                className="bg-[#f9f9fb] rounded-xl border border-gray-200 overflow-hidden"
                data-testid={`faq-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full flex items-start justify-between text-left px-5 py-4 hover:bg-gray-50 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="text-[14.5px] sm:text-[16px] font-semibold text-[#1d1d1f] leading-snug pr-4">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform mt-0.5 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 pt-1">
                    <p className="text-[14px] sm:text-[15px] text-[#3a3a3f] leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Other lifecycle stages */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="other-pillars">
        <h2 className="text-[22px] sm:text-[28px] font-bold text-[#1d1d1f] leading-tight mb-6">
          Other stages of the journey
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {otherPillars.map(({ stage: s, pillar: p }) => (
            <Link
              key={s.slug}
              to={`/lifecycle/${p.urlSlug}`}
              className="group block bg-white border border-gray-200 hover:border-[#1d8efe] rounded-xl p-4 sm:p-5 transition-colors"
              data-testid={`other-pillar-${p.urlSlug}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {s.iconSrc && (
                  <img
                    src={s.iconSrc}
                    alt=""
                    aria-hidden="true"
                    className="w-6 h-6 object-contain"
                    loading="lazy"
                  />
                )}
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#1d8efe]">
                  {s.name}
                </span>
              </div>
              <p className="text-[13.5px] font-semibold text-[#1d1d1f] leading-tight">
                {s.heading}
              </p>
              <p className="text-[12px] text-gray-500 mt-1.5 line-clamp-2 leading-snug">
                {s.intro}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Sources & references block. Cites the regulatory and primary
          sources behind the cost/grant/timeline claims on this page, plus
          links to the BuildPilot internal tools mentioned in the body.
          Two goals: (1) E-E-A-T transparency for Google/AI crawlers, who
          weight cited content much higher than uncited; (2) clear
          legal/copyright posture, all factual claims are verifiable. */}
      {pillar.sources && pillar.sources.length > 0 && (
        <section
          className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 mb-20"
          data-testid="pillar-sources"
        >
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 mb-4">
              Sources & references
            </h2>
            <p className="text-[12.5px] text-gray-500 leading-relaxed mb-4">
              Regulations, cost figures and grant thresholds quoted on this page reference the South Australian Government, the Australian Building Codes Board and federal first-home-buyer schemes current at the time of writing (1 March 2026). All editorial guidance is BuildPilot&rsquo;s own and reflects our independent reading of public sources and our work with Adelaide families through the build journey.
            </p>
            <ul className="space-y-1.5">
              {pillar.sources.map((s, i) => {
                const isExternal = s.url.startsWith('http');
                return (
                  <li key={i} className="text-[12.5px] text-gray-600 leading-snug">
                    <span className="text-gray-400 mr-2">[{i + 1}]</span>
                    {isExternal ? (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-gray-700 hover:text-[#1d8efe] underline-offset-2 hover:underline"
                      >
                        {s.name}
                      </a>
                    ) : (
                      <Link
                        to={s.url}
                        className="text-gray-700 hover:text-[#1d8efe] underline-offset-2 hover:underline"
                      >
                        {s.name}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* Related HBH video: reverse-link into /watch by topic match. */}
      {(() => {
        const relatedEp = findEpisodeForContext({
          title: pillar?.h1 || stage?.title || '',
          body: `${pillar?.intro || ''} ${pillar?.subtitle || ''} ${stage?.description || ''}`,
          topics: pillar?.hbhTopics || stage?.hbhTopics || [],
        });
        return relatedEp ? (
          <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <HbhVideoStrip
              episode={relatedEp}
              eyebrow="Watch: same stage on Home Building Hub"
            />
          </section>
        ) : null;
      })()}

      {/* Soft CTA footer */}
      {stage.softCta && (
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <Link
            to={stage.softCta.href}
            className="block bg-gradient-to-r from-[#fefaf5] to-[#fdf3ea] border border-[#f5e0c5] rounded-2xl px-5 sm:px-7 py-4 sm:py-5 hover:from-[#fdf3ea] hover:to-[#fcecdc] transition-colors"
            data-testid="pillar-soft-cta"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-[14px] sm:text-[15.5px] font-semibold text-[#cc6a1f]">
                {stage.softCta.label}
              </span>
              <ArrowRight className="w-5 h-5 text-[#cc6a1f] flex-shrink-0" />
            </div>
          </Link>
        </section>
      )}

      <section className="py-8 bg-[#f5f5f7]">
        <div className="max-w-[680px] mx-auto px-5 sm:px-6">
          <SponsoredSlot
            surface={`lifecycle-${pillar.stageSlug || 'stage'}`}
            topics={['design', 'planning', 'cost', 'first-home', 'kdr', 'professionals', 'custom-design']}
            size="small"
          />
        </div>
      </section>

      {affiliateLifecycleStage(pillar.stageSlug) && (
        <CompleteYourProject
          stage={affiliateLifecycleStage(pillar.stageSlug)}
          variant="compact"
          limit={1}
          perNeed={15}
          dense
          sideRail
          heading="Products we've matched for this stage"
          intro="The practical gear owners reach for at this point. You buy direct from the supplier at their price."
        />
      )}
    </div>
  );
};

export default LifecyclePillarPage;
